import { Suspense, useEffect, useState } from 'react';
import { useOverlays } from '../../contexts/StoreContext';
import { useAskSarah } from '../../contexts/AccountContext';
import { PageErrorBoundary } from '../../ui/PageErrorBoundary';
import { lazyWithRetry } from '../../ui/lazyWithRetry';

// De vier lades zitten niet meer in de eerste download: ze laden bij de eerste keer openen,
// of eerder, zodra de browser even niets te doen heeft (zie useIdlePrefetch).
const loadCart = () => import('../cart/CartDrawer').then(m => m.CartDrawer);
const loadBooking = () => import('../salon/BookingDrawer').then(m => m.BookingDrawer);
const loadQuiz = () => import('../quiz/SkinQuiz').then(m => m.SkinQuiz);
const loadAsk = () => import('../account/AskSarahDrawer').then(m => m.AskSarahDrawer);
const CartDrawer = lazyWithRetry(loadCart, 'cart-drawer');
const BookingDrawer = lazyWithRetry(loadBooking, 'booking-drawer');
const SkinQuiz = lazyWithRetry(loadQuiz, 'skin-quiz');
const AskSarahDrawer = lazyWithRetry(loadAsk, 'ask-sarah');

/** Hoe lang we wachten als de browser geen requestIdleCallback kent (Safari). */
const IDLE_FALLBACK_MS = 2500;

/**
 * Winkelmand, afspraak, huidadvies en Vraag Sarah.
 *
 * Elke lade wordt pas getekend na de eerste keer openen en blijft daarna staan:
 * zo speelt de sluitanimatie af en blijft de state (quizantwoorden, ingevulde velden) bewaard.
 */
export function LazyOverlays() {
  const {
    cartOpen,
    bookingOpen,
    quizOpen
  } = useOverlays();
  const {
    askOpen
  } = useAskSarah();
  const cart = useOpenedOnce(cartOpen);
  const booking = useOpenedOnce(bookingOpen);
  const quiz = useOpenedOnce(quizOpen);
  const ask = useOpenedOnce(askOpen);
  useIdlePrefetch();
  return <PageErrorBoundary title="Dit venster laadt even niet." data-id="element-7829" data-mp="1tjwbh37trtno">
      <Suspense fallback={null} data-id="element-7830" data-mp="1ti30xcu3cpae">
        {cart && <CartDrawer data-id="element-7831" data-mp="d5xonnxwj5ux" />}
        {booking && <BookingDrawer data-id="element-7832" data-mp="d4v0hnryrhip" />}
        {quiz && <SkinQuiz data-id="element-7833" data-mp="d7qux24lro2e" />}
        {ask && <AskSarahDrawer data-id="element-7834" data-mp="i4h9tm4o8dm8" />}
      </Suspense>
    </PageErrorBoundary>;
}

/** `true` vanaf de eerste keer dat `open` waar was. */
function useOpenedOnce(open: boolean) {
  const [opened, setOpened] = useState(open);
  useEffect(() => {
    if (open) setOpened(true);
  }, [open]);
  return opened || open;
}

/**
 * Laadt de code van de lades vooruit als de browser stil is, zodat de eerste klik
 * op "Winkelmand" niet op het netwerk hoeft te wachten. Fouten negeren we hier:
 * bij het echte openen probeert lazyWithRetry het opnieuw.
 */
function useIdlePrefetch() {
  useEffect(() => {
    const prefetch = () => {
      for (const load of [loadCart, loadQuiz, loadBooking, loadAsk]) load().catch(() => undefined);
    };
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(prefetch, {
        timeout: IDLE_FALLBACK_MS * 2
      });
      return () => window.cancelIdleCallback(id);
    }
    // globalThis: na de `in window`-controle is `window` hier voor TypeScript `never`.
    const id = globalThis.setTimeout(prefetch, IDLE_FALLBACK_MS);
    return () => globalThis.clearTimeout(id);
  }, []);
}